import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "./ui/popover";
import { Button } from "./ui/button";
import { AlertTriangle, Clock, FileWarning, ChevronRight } from "lucide-react";

const ROLES_VISIVEL = ["diretor", "gerente", "supervisao", "operador", "oficina"];

export function SlaAlertBell() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState({ vencidos: 0, urgentes: 0, justificativas_pendentes: 0, total: 0 });
  const intervalRef = useRef(null);

  const visivel = user && ROLES_VISIVEL.includes(user.role);

  useEffect(() => {
    if (!visivel) return;
    const fetch = async () => {
      try {
        const { data: d } = await api.get("/tickets-ops/alertas-sla");
        setData(d);
      } catch (e) { /* silencioso */ }
    };
    fetch();
    intervalRef.current = setInterval(fetch, 60000); // poll 60s
    return () => clearInterval(intervalRef.current);
  }, [visivel]);

  if (!visivel) return null;

  const total = data.total || 0;
  const temVencidos = (data.vencidos || 0) > 0;
  const corBg = temVencidos ? "bg-red-600" : (total > 0 ? "bg-amber-500" : "bg-slate-400");
  const animar = temVencidos ? "animate-pulse" : "";

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          className="relative p-2 hover:bg-slate-100 rounded-md transition"
          data-testid="sla-alert-bell"
          title="Alertas de SLA"
        >
          <AlertTriangle size={18} className={total > 0 ? "text-red-600" : "text-slate-500"} />
          {total > 0 && (
            <span
              className={`absolute -top-0.5 -right-0.5 ${corBg} ${animar} text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1`}
              data-testid="sla-alert-count"
            >
              {total > 99 ? "99+" : total}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-96 p-0" align="end" data-testid="sla-alert-popover">
        <div className="p-3 border-b bg-slate-50">
          <h3 className="font-bold text-sm flex items-center gap-2">
            <AlertTriangle size={14} className="text-orange-600" />
            Alertas SLA · Tickets/Operações
          </h3>
        </div>

        <div className="max-h-96 overflow-y-auto">
          {total === 0 && (
            <div className="p-6 text-center text-xs text-slate-500">
              ✓ Tudo dentro do prazo — sem alertas no momento
            </div>
          )}

          {data.vencidos > 0 && (
            <div className="border-b">
              <div className="px-3 py-2 bg-red-50 border-l-4 border-red-600 flex items-center justify-between">
                <span className="text-xs font-bold text-red-800 flex items-center gap-1">
                  <AlertTriangle size={12} /> VENCIDOS
                </span>
                <span className="text-sm font-bold text-red-700">{data.vencidos}</span>
              </div>
              {(data.lista_vencidos || []).map((t) => (
                <div
                  key={t.id}
                  className="px-3 py-1.5 text-[11px] flex items-center justify-between hover:bg-red-50 cursor-pointer border-b last:border-0"
                  onClick={() => { navigate("/app/tickets-ops"); }}
                  data-testid={`alert-vencido-${t.id}`}
                >
                  <span className="font-mono">#{t.id.slice(0, 6)} · {t.tipo}</span>
                  <span className="text-red-700 font-bold">{t.horas_vencido}h atraso</span>
                </div>
              ))}
            </div>
          )}

          {data.urgentes > 0 && (
            <div className="border-b">
              <div className="px-3 py-2 bg-amber-50 border-l-4 border-amber-500 flex items-center justify-between">
                <span className="text-xs font-bold text-amber-800 flex items-center gap-1">
                  <Clock size={12} /> URGENTES (&lt;12h)
                </span>
                <span className="text-sm font-bold text-amber-700">{data.urgentes}</span>
              </div>
              {(data.lista_urgentes || []).map((t) => (
                <div
                  key={t.id}
                  className="px-3 py-1.5 text-[11px] flex items-center justify-between hover:bg-amber-50 cursor-pointer border-b last:border-0"
                  onClick={() => { navigate("/app/tickets-ops"); }}
                  data-testid={`alert-urgente-${t.id}`}
                >
                  <span className="font-mono">#{t.id.slice(0, 6)} · {t.tipo}</span>
                  <span className="text-amber-700 font-bold">{t.horas_restantes}h restantes</span>
                </div>
              ))}
            </div>
          )}

          {data.justificativas_pendentes > 0 && (
            <div
              className="px-3 py-2 bg-orange-50 border-l-4 border-orange-500 flex items-center justify-between cursor-pointer hover:bg-orange-100"
              onClick={() => navigate("/app/tickets-ops")}
              data-testid="alert-justificativas"
            >
              <span className="text-xs font-bold text-orange-800 flex items-center gap-1">
                <FileWarning size={12} /> JUSTIFICATIVAS PENDENTES
              </span>
              <div className="flex items-center gap-1 text-orange-700">
                <span className="text-sm font-bold">{data.justificativas_pendentes}</span>
                <ChevronRight size={12} />
              </div>
            </div>
          )}
        </div>

        {total > 0 && (
          <div className="p-2 border-t bg-slate-50">
            <Button
              size="sm" className="w-full bg-orange-600 hover:bg-orange-700 text-white h-8 text-xs"
              onClick={() => navigate("/app/tickets-ops")}
              data-testid="alert-ver-todos"
            >
              Abrir Tickets / Operações
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
